import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Sparkles, Camera, Megaphone, FileText, Languages,
  Check, ArrowRight, Building2, Star, Quote, Download, Mail,
} from "lucide-react";
import { triggerInstall } from "@/lib/pwa";

const HERO = "https://images.unsplash.com/photo-1757359056339-22968344cce6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwZXh0ZXJpb3IlMjB0d2lsaWdodHxlbnwwfHx8fDE3ODIzOTY2ODR8MA&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG = "https://images.unsplash.com/photo-1757356657991-c3fd6e2e812e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwZXh0ZXJpb3IlMjB0d2lsaWdodHxlbnwwfHx8fDE3ODIzOTY2ODR8MA&ixlib=rb-4.1.0&q=85";
const TESTIMONIAL_IMG = "https://images.pexels.com/photos/8292786/pexels-photo-8292786.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const features = [
  { icon: FileText, title: "MLS-Ready Descriptions", body: "Turn beds, baths, and a few features into vivid, high-converting listing copy in seconds." },
  { icon: ShieldCheck, title: "Fair Housing Checker", body: "Every listing is scanned for risky language and flagged before you publish. Compliance, built in." },
  { icon: Camera, title: "Photo-to-Copy", body: "Upload a listing photo and let AI infer the features and draft the description for you." },
  { icon: Megaphone, title: "Social Captions", body: "Three scroll-stopping captions per listing — punchy, lifestyle, and urgency-driven." },
  { icon: Sparkles, title: "Headlines & Re-spins", body: "Five irresistible headlines and instant tone rewrites for any buyer or vibe." },
  { icon: Languages, title: "Agent Voice Profiles", body: "Save your style once and every output instantly sounds like you." },
];

const tiers = [
  { id: "starter", name: "Starter", price: 29, tagline: "New or part-time agents testing the waters.",
    perks: [
      "Up to 10 listings / month",
      "Full marketing kit: description, neighborhood, social, headlines, email, video script & flyer",
      "Fair Housing compliance checker on every listing",
      "Photo-to-copy & smart feature detection",
      "Address auto-fill + voice-to-listing dictation",
      "Agent voice profile, tone re-spin & buyer targeting",
    ] },
  { id: "pro", name: "Pro", price: 69, popular: true, tagline: "Active full-time agents who want it all.",
    perks: [
      "Everything in Starter, plus:",
      "Unlimited listings",
      "AI Coach mode — live engagement score + 1-tap improve",
      "Branded social graphics & \"Just Listed\" video reels",
      "1-click property landing pages with lead capture",
      "Multilingual output in 6 languages",
      "Full toolkit: offer letters, ad copy, testimonials, buyer-match & follow-up sequences",
      "Share to Facebook, LinkedIn, X & Instagram",
    ] },
  { id: "brokerage", name: "Brokerage / Elite", price: 249, tagline: "Teams & top producers who need scale.",
    perks: [
      "Everything in Pro, plus:",
      "Bulk CSV listing generation",
      "Brokerage Intelligence: Performance Predictor & A/B Test Lab",
      "Compliance presets — auto-enforce banned phrases",
      "Performance Analytics: views & leads over time",
      "White-label branding & team seats",
      "Priority support",
    ] },
];

export default function Landing() {
  const navigate = useNavigate();
  const go = () => navigate("/login");
  const onInstall = async () => {
    const r = await triggerInstall();
    if (r === "ios") toast.info("On iPhone/iPad: tap the Share icon, then \"Add to Home Screen\".");
    else if (r === "installed") toast.success("PenhausAI is already installed on this device.");
    else if (r === "unavailable") toast.info("Open this site in Chrome or Edge (or your phone browser) and use the browser's Install option.");
  };

  return (
    <div className="bg-ink text-white min-h-screen font-sans overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/50 border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <Building2 className="h-5 w-5 text-champagne" />
            <span className="font-display text-xl tracking-tight">Penhaus<span className="text-champagne">AI</span></span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#proof" className="hover:text-white transition-colors">Agents</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={go} data-testid="nav-login-btn" className="text-sm text-zinc-300 hover:text-white transition-colors">Sign in</button>
            <Button onClick={go} data-testid="nav-cta-btn" className="bg-champagne text-black hover:bg-[#F3E5AB] rounded-sm font-semibold">Start free</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="Luxury home at dusk" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/85 to-[#0B0E14]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-12 gap-8 pt-24">
          <div className="lg:col-span-7 fade-up">
            <span className="text-xs uppercase tracking-[0.25em] text-champagne">For real estate agents & teams</span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-6">
              Listings that sell the<br /><span className="italic text-champagne">lifestyle</span>, not just the square footage.
            </h1>
            <p className="text-lg text-zinc-300 mt-7 max-w-xl leading-relaxed">
              PenhausAI turns a few property details into polished MLS descriptions, neighborhood highlights, and social posts — every one scanned for Fair Housing compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-9">
              <Button onClick={go} data-testid="hero-cta-btn" size="lg" className="bg-champagne text-black hover:bg-[#F3E5AB] rounded-sm font-semibold text-base h-12 px-8">
                Write my first listing <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} variant="outline" size="lg" className="rounded-sm h-12 px-8 border-white/20 bg-white/5 text-white hover:bg-white/10">
                View pricing
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-zinc-400">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-champagne" /> Fair Housing compliant</span>
              <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-champagne" /> 7-day Pro trial</span>
            </div>
          </div>
        </div>
      </header>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-28">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.25em] text-champagne">The complete listing toolkit</span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-5 leading-tight">Own the entire listing-marketing workflow.</h2>
          <p className="text-zinc-400 mt-5 text-lg">Not a generic chatbot — a purpose-built assistant trained on what actually converts buyers.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 mt-14 border border-hairline rounded-md overflow-hidden">
          {features.map((f) => (
            <div key={f.title} className="bg-ink-surface p-8 hover:bg-[#1b212c] transition-colors" data-testid={`feature-${f.title}`}>
              <f.icon className="h-7 w-7 text-champagne" />
              <h3 className="text-xl mt-5 font-semibold">{f.title}</h3>
              <p className="text-zinc-400 mt-3 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Showcase */}
      <section className="max-w-7xl mx-auto px-6 pb-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-lg overflow-hidden border border-hairline">
            <img src={FEATURE_IMG} alt="High-end listing" className="w-full h-[420px] object-cover" />
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-champagne">Compliance as a feature</span>
            <h2 className="font-display text-4xl tracking-tight mt-5 leading-tight">Never publish risky copy again.</h2>
            <p className="text-zinc-400 mt-5 text-lg leading-relaxed">
              Agents fear costly Fair Housing mistakes. PenhausAI scans every description for steering language and protected-class references, flags it inline, and suggests a compliant rewrite — peace of mind on every listing.
            </p>
            <ul className="mt-7 space-y-3">
              {["Inline flagging with plain-English explanations", "Suggested compliant alternatives", "Audit-ready history of every generation"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-zinc-200"><Check className="h-5 w-5 text-champagne shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section id="proof" className="bg-ink-surface border-y border-hairline">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <img src={TESTIMONIAL_IMG} alt="Realtor" className="rounded-lg w-full h-[360px] object-cover border border-hairline" />
          </div>
          <div className="lg:col-span-8">
            <div className="flex gap-1 text-champagne mb-5">{[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-champagne" />)}</div>
            <Quote className="h-10 w-10 text-champagne/40" />
            <p className="font-display text-2xl sm:text-3xl leading-snug mt-4">
              "I used to spend 40 minutes per listing. Now I drop in the details, get copy that sounds like me, and the compliance check means I publish without second-guessing."
            </p>
            <p className="text-zinc-400 mt-6">— Marisa Lane, Top Producer · Coastal Realty Group</p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-28">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.25em] text-champagne">Pricing</span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-5">Plans that grow with your volume.</h2>
          <p className="text-zinc-400 mt-4 text-lg">Start free. Upgrade when listings pile up.</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-6 mt-16 items-start">
          {tiers.map((t) => (
            <div key={t.id} data-testid={`pricing-tier-${t.id}`}
              className={`relative rounded-lg p-8 border ${t.popular ? "border-champagne bg-ink-surface lg:-mt-4 lg:mb-4" : "border-hairline bg-ink-surface/60"}`}>
              {t.popular && <div className="absolute -top-3 left-8 bg-champagne text-black text-xs font-bold px-3 py-1 rounded-sm uppercase tracking-wide">Most Popular</div>}
              <h3 className="text-xl font-semibold">{t.name}</h3>
              <p className="text-zinc-400 text-sm mt-1">{t.tagline}</p>
              <div className="mt-6 flex items-end gap-1">
                <span className="font-display text-5xl">${t.price}</span>
                <span className="text-zinc-400 mb-2">/mo</span>
              </div>
              <Button onClick={go} data-testid={`pricing-tier-${t.id}-btn`}
                className={`w-full mt-6 rounded-sm font-semibold ${t.popular ? "bg-champagne text-black hover:bg-[#F3E5AB]" : "bg-white/10 text-white hover:bg-white/20"}`}>
                {t.popular ? "Start 7-day trial" : "Choose " + t.name}
              </Button>
              <ul className="mt-8 space-y-3">
                {t.perks.map((p) => (
                  p.endsWith(":")
                    ? <li key={p} className="text-xs font-semibold uppercase tracking-wide text-champagne pt-1">{p}</li>
                    : <li key={p} className="flex gap-3 text-sm text-zinc-300"><Check className="h-4 w-4 text-champagne shrink-0 mt-0.5" /> {p}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-hairline">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-5 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-champagne" />
            <span className="font-display text-lg text-white">Penhaus<span className="text-champagne">AI</span></span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-x-6 gap-y-3">
            <button onClick={onInstall} data-testid="footer-install-app"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-champagne transition-colors">
              <Download className="h-4 w-4" /> Install app
            </button>
            <a href="mailto:support@penhausai.com" data-testid="footer-support-email"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-champagne transition-colors">
              <Mail className="h-4 w-4" /> support@penhausai.com
            </a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-10 -mt-4 text-sm text-zinc-500 text-center md:text-left">
          <p>© {new Date().getFullYear()} PenhausAI. The agent's complete listing assistant.</p>
        </div>
      </footer>
    </div>
  );
}
